import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {Helmet} from 'react-helmet-async';
import {
    ErrorBoundary,
    NotificationContainer,
    NotificationPropTypes,
} from '@spothero/ui/backlog';
import {clearNotification} from 'store/notification/notification-actions';
import {DESTINATION_DATA, USER_DATA} from 'proptypes';
import ApplicationMeta from 'seo/application-meta';
import GDPRModal from 'common/gdpr-modal';
import HeaderDesktop from 'common/header/desktop';
import HeaderMobile from 'common/header/mobile';
import HeaderCheckout from 'common/header/checkout';
import Footer from 'common/footer/default';
import FooterCheckout from 'common/footer/checkout';
import withConfig from 'config/withConfig';
import Navigator from './Navigator';

import '../sass/main.scss';

class App extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        pageType: PropTypes.string,
        cloudfrontViewerCountryCode: PropTypes.string,
        citySlugs: PropTypes.array,
        collapsedNavData: PropTypes.array.isRequired,
        expandedNavData: PropTypes.array.isRequired,
        footerData: PropTypes.array.isRequired,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        notification: PropTypes.shape(NotificationPropTypes),
        clearNotifications: PropTypes.func.isRequired,
        isMonthly: PropTypes.bool.isRequired,
        serverContext: PropTypes.object,
        user: PropTypes.shape(USER_DATA).isRequired,
    };
    state = {
        showGDPRModal: false,
        userCountryCode: null,
    };

    componentDidMount() {
        this._checkIfForeignUser();
    }

    _onGDPRModalHidden = () => {
        this.setState({
            showGDPRModal: false,
        });
    };

    _checkIfForeignUser() {
        const {cloudfrontViewerCountryCode} = this.props;
        const isForeignUser =
            cloudfrontViewerCountryCode &&
            cloudfrontViewerCountryCode !== 'US' &&
            cloudfrontViewerCountryCode !== 'CA';

        if (isForeignUser) {
            this.setState({
                showGDPRModal: true,
                userCountryCode: cloudfrontViewerCountryCode,
            });
        }
    }

    render() {
        const {
            isMobile,
            notification,
            clearNotifications,
            pageType,
            citySlugs,
            user: {email},
            destination,
            isMonthly,
            expandedNavData,
            collapsedNavData,
            footerData,
            serverContext,
        } = this.props;
        const {showGDPRModal, userCountryCode} = this.state;
        const isCheckout = pageType === 'checkout';
        const HeaderComponent = isCheckout
            ? HeaderCheckout
            : isMobile
            ? HeaderMobile
            : HeaderDesktop;
        const FooterComponent = isCheckout ? FooterCheckout : Footer;

        return (
            <ErrorBoundary>
                <ApplicationMeta />
                {isMobile ? (
                    <Helmet>
                        <meta httpEquiv="cleartype" content="on" />
                        <meta
                            name="viewport"
                            content="width=device-width, initial-scale=1, minimal-ui"
                        />
                        <meta name="HandheldFriendly" content="True" />
                        <meta name="MobileOptimized" content="320" />
                        <meta name="mobile-web-app-capable" content="yes" />
                        <meta
                            name="apple-mobile-web-app-capable"
                            content="yes"
                        />
                        <meta
                            name="apple-mobile-web-app-status-bar-style"
                            content="black"
                        />
                        <meta
                            name="apple-mobile-web-app-title"
                            content="SpotHero"
                        />
                        <meta name="theme-color" content="#0082ff" />
                    </Helmet>
                ) : (
                    <Helmet>
                        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                        <meta
                            name="viewport"
                            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
                        />
                    </Helmet>
                )}
                <div className="App">
                    <NotificationContainer
                        notification={notification}
                        onQueueEmpty={clearNotifications}
                    />
                    <HeaderComponent
                        expandedData={expandedNavData}
                        collapsedData={collapsedNavData}
                    />
                    <Navigator
                        extraProps={{
                            pageType,
                            configData: {isMobile, citySlugs},
                            isMonthly,
                            destination,
                            serverContext,
                        }}
                    />
                    <FooterComponent data={footerData} email={email} />
                    {showGDPRModal && (
                        <GDPRModal
                            onHidden={this._onGDPRModalHidden}
                            countryCode={userCountryCode}
                        />
                    )}
                </div>
            </ErrorBoundary>
        );
    }
}

const mapStateToProps = state => {
    const {
        destination: {data: destinationData},
        notification,
        searchRequest: {isSearchPage, pageType, monthly: isMonthly},
        user: {data: user},
    } = state;

    return {
        destination: destinationData,
        notification,
        user,
        pageType,
        isMonthly,
        isSearchPage,
    };
};

const mapDispatchToProps = {
    clearNotifications: clearNotification,
};

const enhance = compose(
    withConfig(['citySlugs', 'cloudfrontViewerCountryCode', 'isMobile']),
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(App);
